import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEvents } from '../../context/EventsContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  ArrowLeft, Save, Trash2,
  Square, Circle, Check, GripVertical, Minus, Armchair, RotateCcw
} from 'lucide-react';

import { getChairPositionsWithOverrides } from '../../utils/chairPositions';

const generateChairs = (tableId, tableLabel, count, defaultPrice) => {
  const chairs = [];
  for (let i = 0; i < count; i++) {
    const letter = String.fromCharCode(65 + i);
    chairs.push({
      id: `ch-${tableId}-${Date.now()}-${i}`,
      tableId,
      label: `${tableLabel}${letter}`,
      price: defaultPrice,
      status: 'available'
    });
  }
  return chairs;
};

const LayoutEditor = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { getEventDetail, getLayout, updateLayout } = useEvents();
  const containerRef = useRef(null);
  
  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [layout, setLayout] = useState({ tables: [], chairs: [], features: [] });
  const [selectedItem, setSelectedItem] = useState(null);
  const [isAddingTable, setIsAddingTable] = useState(false);
  const [isAddingFeature, setIsAddingFeature] = useState(false);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);

  const [newTable, setNewTable] = useState({
    label: '',
    chairCount: 4,
    defaultPrice: 100,
    shape: 'square'
  });

  const [newFeature, setNewFeature] = useState({ type: 'stage', label: '' });

  useEffect(() => {
    const load = async () => {
      try {
        const eventData = await getEventDetail(eventId);
        setEvent(eventData);
        const layoutData = eventData?.layout || await getLayout(eventId);
        setLayout({ tables: layoutData?.tables || [], chairs: layoutData?.chairs || [], features: layoutData?.features || [] });
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingEvent(false);
      }
    };
    if (eventId) load();
  }, [eventId, getEventDetail, getLayout]);

  // Group chairs by table
  const chairsByTable = useMemo(() => {
    const map = {};
    (layout.chairs || []).forEach(c => {
      if (!map[c.tableId]) map[c.tableId] = [];
      map[c.tableId].push(c);
    });
    return map;
  }, [layout.chairs]);

  const handleCanvasClick = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    if (isAddingTable) {
      setNewTable(prev => ({ ...prev, x, y }));
      setShowTableDialog(true);
      setIsAddingTable(false);
    } else if (isAddingFeature) {
      const feature = {
        ...newFeature,
        id: `f-${Date.now()}`,
        x: Math.max(0, x - 7), y: Math.max(0, y - 5),
        width: 14, height: 10
      };
      setLayout(prev => ({ ...prev, features: [...prev.features, feature] }));
      setIsAddingFeature(false);
    } else {
      setSelectedItem(null);
    }
  };

  const handleAddTable = () => {
    const tableId = `t-${Date.now()}`;
    const table = {
      id: tableId,
      x: newTable.x - 6, y: newTable.y - 8,
      width: 12, height: 16,
      shape: newTable.shape,
      label: newTable.label
    };
    const chairs = generateChairs(tableId, newTable.label, newTable.chairCount, newTable.defaultPrice);
    
    setLayout(prev => ({
      ...prev,
      tables: [...prev.tables, table],
      chairs: [...(prev.chairs || []), ...chairs]
    }));
    setShowTableDialog(false);
    setNewTable({ label: '', chairCount: 4, defaultPrice: 100, shape: 'square' });
  };

  const handleUpdateTable = (tableId, updates) => {
    setLayout(prev => ({
      ...prev,
      tables: prev.tables.map(t => t.id === tableId ? { ...t, ...updates } : t)
    }));
  };

  const handleDeleteTable = (tableId) => {
    setLayout(prev => ({
      ...prev,
      tables: prev.tables.filter(t => t.id !== tableId),
      chairs: (prev.chairs || []).filter(c => c.tableId !== tableId)
    }));
    setSelectedItem(null);
  };

  const handleUpdateChair = (chairId, updates) => {
    setLayout(prev => ({
      ...prev,
      chairs: (prev.chairs || []).map(c => c.id === chairId ? { ...c, ...updates } : c)
    }));
  };

  const handleResetChairPosition = (chairId) => {
    handleUpdateChair(chairId, { posX: null, posY: null });
  };

  const handleNudgeChair = (chairId, dx, dy) => {
    const chair = (layout.chairs || []).find(c => c.id === chairId);
    if (!chair) return;
    const table = layout.tables.find(t => t.id === chair.tableId);
    if (!table) return;
    // If no override yet, seed with auto position
    let baseX = chair.posX;
    let baseY = chair.posY;
    if (baseX == null || baseY == null) {
      const tChairs = (layout.chairs || []).filter(c => c.tableId === table.id);
      const positions = getChairPositionsWithOverrides(table, tChairs);
      const idx = tChairs.findIndex(c => c.id === chairId);
      const pos = positions[idx] || { x: table.x, y: table.y };
      baseX = pos.x;
      baseY = pos.y;
    }
    handleUpdateChair(chairId, {
      posX: Math.max(0, Math.min(100, baseX + dx)),
      posY: Math.max(0, Math.min(100, baseY + dy)),
    });
  };

  const handleDeleteFeature = (featureId) => {
    setLayout(prev => ({ ...prev, features: prev.features.filter(f => f.id !== featureId) }));
    setSelectedItem(null);
  };

  const handleRegenerateChairs = (tableId, count, defaultPrice) => {
    const table = layout.tables.find(t => t.id === tableId);
    if (!table) return;
    const newChairs = generateChairs(tableId, table.label, count, defaultPrice);
    setLayout(prev => ({
      ...prev,
      chairs: [...(prev.chairs || []).filter(c => c.tableId !== tableId), ...newChairs]
    }));
  };

  // Drag (mouse + touch)
  const getEventPoint = (e) => {
    if (e.touches && e.touches[0]) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    if (e.changedTouches && e.changedTouches[0]) return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
    return { x: e.clientX, y: e.clientY };
  };

  const handleMouseDown = (e, item, type) => {
    if (e.target.dataset.resize) return;
    e.stopPropagation();
    if (e.cancelable) e.preventDefault();
    const el = e.currentTarget;
    const point = getEventPoint(e);
    // For chairs we drag from their CURRENT rendered position (use override or auto)
    let startItem = { ...item };
    if (type === 'chair' && (item.posX == null || item.posY == null)) {
      const table = layout.tables.find(t => t.id === item.tableId);
      if (table) {
        const tChairs = (layout.chairs || []).filter(c => c.tableId === table.id);
        const positions = getChairPositionsWithOverrides(table, tChairs);
        const idx = tChairs.findIndex(c => c.id === item.id);
        const pos = positions[idx];
        if (pos) startItem = { ...startItem, x: pos.x, y: pos.y };
      }
    } else if (type === 'chair') {
      startItem = { ...startItem, x: item.posX, y: item.posY };
    }
    dragRef.current = { item: startItem, type, startX: point.x, startY: point.y, el };
    setSelectedItem({ ...item, type });
  };

  const handleResizeDown = (e, item, type) => {
    e.stopPropagation();
    if (e.cancelable) e.preventDefault();
    const el = e.currentTarget.parentElement;
    const point = getEventPoint(e);
    resizeRef.current = { item: { ...item }, type, startX: point.x, startY: point.y, el };
  };

  const handleMouseMove = useCallback((e) => {
    const drag = dragRef.current;
    if (drag && containerRef.current) {
      const point = getEventPoint(e);
      const rect = containerRef.current.getBoundingClientRect();
      const dx = ((point.x - drag.startX) / rect.width) * 100;
      const dy = ((point.y - drag.startY) / rect.height) * 100;
      const newX = Math.max(0, drag.item.x + dx);
      const newY = Math.max(0, drag.item.y + dy);
      drag.el.style.left = `${newX}%`;
      drag.el.style.top = `${newY}%`;
      drag._lastX = newX;
      drag._lastY = newY;
      if (e.cancelable) e.preventDefault();
      return;
    }
    const rz = resizeRef.current;
    if (rz && containerRef.current) {
      const point = getEventPoint(e);
      const rect = containerRef.current.getBoundingClientRect();
      const dw = ((point.x - rz.startX) / rect.width) * 100;
      const dh = ((point.y - rz.startY) / rect.height) * 100;
      rz.el.style.width = `${Math.max(4, rz.item.width + dw)}%`;
      rz.el.style.height = `${Math.max(4, rz.item.height + dh)}%`;
      rz._lastW = Math.max(4, rz.item.width + dw);
      rz._lastH = Math.max(4, rz.item.height + dh);
      if (e.cancelable) e.preventDefault();
    }
  }, [layout.chairs, layout.tables]);

  const handleMouseUp = useCallback(() => {
    const drag = dragRef.current;
    if (drag && drag._lastX !== undefined) {
      const { id } = drag.item;
      setLayout(prev => {
        if (drag.type === 'table') return { ...prev, tables: prev.tables.map(t => t.id === id ? { ...t, x: drag._lastX, y: drag._lastY } : t) };
        if (drag.type === 'chair') return { ...prev, chairs: (prev.chairs || []).map(c => c.id === id ? { ...c, posX: drag._lastX, posY: drag._lastY } : c) };
        return { ...prev, features: prev.features.map(f => f.id === id ? { ...f, x: drag._lastX, y: drag._lastY } : f) };
      });
    }
    dragRef.current = null;
    const rz = resizeRef.current;
    if (rz && rz._lastW !== undefined) {
      const { id } = rz.item;
      setLayout(prev => {
        if (rz.type === 'table') return { ...prev, tables: prev.tables.map(t => t.id === id ? { ...t, width: rz._lastW, height: rz._lastH } : t) };
        return { ...prev, features: prev.features.map(f => f.id === id ? { ...f, width: rz._lastW, height: rz._lastH } : f) };
      });
    }
    resizeRef.current = null;
  }, []);

  const handleSave = async () => {
    await updateLayout(eventId, layout);
    navigate('/admin/dashboard');
  };

  if (loadingEvent) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-muted-foreground">Cargando...</div>;
  }

  if (!event) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-muted-foreground">Evento no encontrado</div>;
  }

  const selectedTableData = selectedItem?.type === 'table' ? layout.tables.find(t => t.id === selectedItem.id) : null;
  const selectedChairData = selectedItem?.type === 'chair' ? (layout.chairs || []).find(c => c.id === selectedItem.id) : null;
  const selectedTableChairCount = selectedTableData ? (chairsByTable[selectedTableData.id] || []).length : 0;

  return (
    <div
      data-testid="layout-editor"
      className="min-h-screen bg-[#050505]"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchMove={handleMouseMove}
      onTouchEnd={handleMouseUp}
      onTouchCancel={handleMouseUp}
    >
      {/* Header */}
      <header className="border-b border-border bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Volver</span>
            </Button>
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-serif font-semibold text-foreground truncate">Editor de Layout</h1>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">{event.name}</p>
            </div>
          </div>
          <Button data-testid="save-layout-btn" onClick={handleSave} className="bg-primary hover:bg-accent shrink-0">
            <Save className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Guardar Layout</span><span className="sm:hidden">Guardar</span>
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="grid lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Toolbar */}
          <div className="lg:col-span-1 space-y-4 order-2 lg:order-1">
            <div className="bg-secondary border border-border rounded-lg p-4 space-y-4">
              <h3 className="font-medium text-foreground">Agregar</h3>
              <Button
                data-testid="add-table-btn"
                variant={isAddingTable ? "default" : "outline"}
                className={`w-full justify-start ${isAddingTable ? 'bg-primary' : 'border-border'}`}
                onClick={() => { setIsAddingTable(!isAddingTable); setIsAddingFeature(false); }}
              >
                <Square className="w-4 h-4 mr-2" />
                {isAddingTable ? 'Click en el plano...' : 'Agregar Mesa + Sillas'}
              </Button>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Tipo de elemento:</Label>
                <Select value={newFeature.type} onValueChange={(v) => setNewFeature({ ...newFeature, type: v })}>
                  <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stage">Escenario</SelectItem>
                    <SelectItem value="bar">Bar</SelectItem>
                    <SelectItem value="entrance">Entrada</SelectItem>
                    <SelectItem value="kitchen">Cocina</SelectItem>
                    <SelectItem value="restroom">Baños</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  data-testid="add-feature-btn"
                  variant={isAddingFeature ? "default" : "outline"}
                  className={`w-full justify-start ${isAddingFeature ? 'bg-blue-600' : 'border-border'}`}
                  onClick={() => { setIsAddingFeature(!isAddingFeature); setIsAddingTable(false); }}
                >
                  <Minus className="w-4 h-4 mr-2" />
                  {isAddingFeature ? 'Click en el plano...' : 'Agregar Elemento'}
                </Button>
              </div>
            </div>

            {/* Table properties */}
            {selectedTableData && (
              <div className="bg-secondary border border-border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-foreground">Mesa</h3>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteTable(selectedItem.id)} className="text-destructive hover:text-destructive hover:bg-destructive/10">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Nombre</Label>
                  <Input value={selectedTableData.label} onChange={(e) => handleUpdateTable(selectedItem.id, { label: e.target.value })} className="bg-background border-border h-8 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Forma</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => handleUpdateTable(selectedItem.id, { shape: 'square' })}
                      className={`flex items-center justify-center gap-1.5 p-2 rounded border text-xs transition-all ${(selectedTableData.shape || 'square') === 'square' ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:border-primary/40'}`}>
                      <Square className="w-3.5 h-3.5" /> Cuadrada
                    </button>
                    <button type="button" onClick={() => handleUpdateTable(selectedItem.id, { shape: 'circle' })}
                      className={`flex items-center justify-center gap-1.5 p-2 rounded border text-xs transition-all ${selectedTableData.shape === 'circle' ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:border-primary/40'}`}>
                      <Circle className="w-3.5 h-3.5" /> Circular
                    </button>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Sillas: {selectedTableChairCount}</Label>
                  <p className="text-[10px] text-muted-foreground">Selecciona una silla en el plano para editar su precio</p>
                </div>
              </div>
            )}

            {/* Chair properties */}
            {selectedChairData && (
              <div className="bg-secondary border border-border rounded-lg p-4 space-y-3">
                <h3 className="font-medium text-foreground flex items-center gap-2">
                  <Armchair className="w-4 h-4 text-primary" />
                  Silla {selectedChairData.label}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {layout.tables.find(t => t.id === selectedChairData.tableId)?.label}
                </p>
                <div className="space-y-1">
                  <Label className="text-xs">Precio</Label>
                  <Input
                    type="number" min="0"
                    value={selectedChairData.price}
                    onChange={(e) => handleUpdateChair(selectedItem.id, { price: parseInt(e.target.value) || 0 })}
                    className="bg-background border-border h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Estado</Label>
                  <Select value={selectedChairData.status} onValueChange={(v) => handleUpdateChair(selectedItem.id, { status: v })}>
                    <SelectTrigger className="bg-background border-border h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Disponible</SelectItem>
                      <SelectItem value="reserved">Reservada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Micro-adjust position */}
                <div className="space-y-2 pt-2 border-t border-border">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Ajuste de posición</Label>
                    {(selectedChairData.posX != null || selectedChairData.posY != null) && (
                      <button
                        type="button"
                        data-testid="reset-chair-position"
                        onClick={() => handleResetChairPosition(selectedItem.id)}
                        className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1"
                      >
                        <RotateCcw className="w-3 h-3" />Reset
                      </button>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground/70">
                    Arrastra la silla en el plano, usa las flechas o ingresa valores precisos (0–100%).
                  </p>
                  <div className="grid grid-cols-3 gap-1.5">
                    <div />
                    <button type="button" data-testid="nudge-up" onClick={() => handleNudgeChair(selectedItem.id, 0, -0.5)}
                      className="h-7 rounded border border-border bg-background hover:bg-muted text-muted-foreground text-xs">↑</button>
                    <div />
                    <button type="button" data-testid="nudge-left" onClick={() => handleNudgeChair(selectedItem.id, -0.5, 0)}
                      className="h-7 rounded border border-border bg-background hover:bg-muted text-muted-foreground text-xs">←</button>
                    <div className="h-7 rounded border border-dashed border-border flex items-center justify-center text-[9px] text-muted-foreground/60">0.5%</div>
                    <button type="button" data-testid="nudge-right" onClick={() => handleNudgeChair(selectedItem.id, 0.5, 0)}
                      className="h-7 rounded border border-border bg-background hover:bg-muted text-muted-foreground text-xs">→</button>
                    <div />
                    <button type="button" data-testid="nudge-down" onClick={() => handleNudgeChair(selectedItem.id, 0, 0.5)}
                      className="h-7 rounded border border-border bg-background hover:bg-muted text-muted-foreground text-xs">↓</button>
                    <div />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">X (%)</Label>
                      <Input
                        type="number" step="0.1" min="0" max="100"
                        data-testid="chair-pos-x"
                        value={selectedChairData.posX != null ? Number(selectedChairData.posX).toFixed(1) : ''}
                        placeholder="auto"
                        onChange={(e) => {
                          const v = e.target.value;
                          handleUpdateChair(selectedItem.id, { posX: v === '' ? null : parseFloat(v) });
                        }}
                        className="bg-background border-border h-7 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">Y (%)</Label>
                      <Input
                        type="number" step="0.1" min="0" max="100"
                        data-testid="chair-pos-y"
                        value={selectedChairData.posY != null ? Number(selectedChairData.posY).toFixed(1) : ''}
                        placeholder="auto"
                        onChange={(e) => {
                          const v = e.target.value;
                          handleUpdateChair(selectedItem.id, { posY: v === '' ? null : parseFloat(v) });
                        }}
                        className="bg-background border-border h-7 text-xs"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedItem?.type === 'feature' && (
              <div className="bg-secondary border border-border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-foreground">Elemento</h3>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteFeature(selectedItem.id)} className="text-destructive hover:text-destructive hover:bg-destructive/10">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">Arrastra para mover · Esquina para redimensionar</p>
              </div>
            )}

            {/* Legend */}
            <div className="bg-secondary border border-border rounded-lg p-4 space-y-2">
              <h3 className="font-medium text-foreground text-sm">Leyenda</h3>
              <div className="space-y-1.5 text-xs">
                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-emerald-800/60 border border-emerald-600/60" /><span className="text-muted-foreground">Silla Disponible</span></div>
                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-muted/40 border border-border/50" /><span className="text-muted-foreground">Silla Reservada</span></div>
                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-primary border border-primary" /><span className="text-muted-foreground">Seleccionada</span></div>
                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-muted/20 border border-border/40" /><span className="text-muted-foreground">Mesa (referencia)</span></div>
              </div>
            </div>
          </div>

          {/* Canvas */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            <div className="bg-secondary border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-foreground">Plano del Restaurante</h3>
                <p className="text-xs text-muted-foreground">
                  {layout.tables.length} mesas · {(layout.chairs || []).length} sillas
                </p>
              </div>
              
              <div
                ref={containerRef}
                data-testid="layout-canvas"
                onClick={handleCanvasClick}
                className={`relative w-full bg-background/50 rounded-lg border border-border overflow-hidden select-none ${isAddingTable || isAddingFeature ? 'cursor-crosshair' : 'cursor-default'}`}
                style={{ paddingBottom: '60%' }}
              >
                {/* Grid */}
                <div className="absolute inset-0 pointer-events-none" style={{
                  backgroundImage: `linear-gradient(rgba(234,88,12,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(234,88,12,0.05) 1px,transparent 1px)`,
                  backgroundSize: '5% 5%'
                }} />

                {/* Features */}
                {layout.features.map((feature) => {
                  const isSel = selectedItem?.id === feature.id;
                  return (
                    <div key={feature.id} data-testid={`editor-feature-${feature.id}`}
                      onMouseDown={(e) => handleMouseDown(e, feature, 'feature')}
                      onTouchStart={(e) => handleMouseDown(e, feature, 'feature')}
                      onClick={(e) => { e.stopPropagation(); setSelectedItem({ ...feature, type: 'feature' }); }}
                      className={`absolute flex items-center justify-center rounded text-xs cursor-move touch-none ${isSel ? 'bg-blue-500/30 border-2 border-blue-500 text-blue-300' : 'bg-muted/50 border border-border text-muted-foreground hover:border-blue-500/50'}`}
                      style={{ left: `${feature.x}%`, top: `${feature.y}%`, width: `${feature.width}%`, height: `${feature.height}%` }}>
                      <GripVertical className="w-3 h-3 mr-1 opacity-50" />{feature.label || feature.type}
                      {isSel && <div data-resize="true" onMouseDown={(e) => handleResizeDown(e, feature, 'feature')} onTouchStart={(e) => handleResizeDown(e, feature, 'feature')} className="absolute -bottom-1 -right-1 w-3 h-3 bg-blue-500 rounded-sm cursor-se-resize z-20" />}
                    </div>
                  );
                })}

                {/* Tables - rendered AFTER chairs so they are on top for clicking */}
                {layout.tables.map((table) => {
                  const isSel = selectedItem?.id === table.id && selectedItem?.type === 'table';
                  return (
                    <div key={table.id} data-testid={`editor-table-${table.id}`}
                      onMouseDown={(e) => handleMouseDown(e, table, 'table')}
                      onTouchStart={(e) => handleMouseDown(e, table, 'table')}
                      onClick={(e) => { e.stopPropagation(); setSelectedItem({ ...table, type: 'table' }); }}
                      className={`absolute flex flex-col items-center justify-center cursor-move z-[5] touch-none
                        ${table.shape === 'circle' ? 'rounded-full' : 'rounded-lg'}
                        ${isSel ? 'bg-primary/20 text-primary border-2 border-primary/60 z-10' : 'bg-muted/20 text-muted-foreground/50 border border-border/40 hover:border-primary/30'}`}
                      style={{ left: `${table.x}%`, top: `${table.y}%`, width: `${table.width}%`, height: `${table.height}%` }}>
                      <span className="text-[10px] font-medium pointer-events-none">{table.label}</span>
                      {isSel && <div data-resize="true" onMouseDown={(e) => handleResizeDown(e, table, 'table')} onTouchStart={(e) => handleResizeDown(e, table, 'table')} className="absolute -bottom-2.5 -right-2.5 w-5 h-5 bg-primary rounded cursor-se-resize z-[35] border-2 border-white/50 shadow-lg" />}
                    </div>
                  );
                })}

                {/* Chairs - draggable for fine-tuning */}
                {layout.tables.map((table) => {
                  const tChairs = chairsByTable[table.id] || [];
                  const positions = getChairPositionsWithOverrides(table, tChairs);
                  const tableIsSelected = selectedItem?.id === table.id && selectedItem?.type === 'table';
                  return tChairs.map((chair, idx) => {
                    const pos = positions[idx];
                    if (!pos) return null;
                    const isSel = selectedItem?.id === chair.id && selectedItem?.type === 'chair';
                    const isAvailable = chair.status === 'available';
                    const hasOverride = chair.posX != null && chair.posY != null;
                    return (
                      <div
                        key={chair.id}
                        data-testid={`editor-chair-${chair.id}`}
                        onMouseDown={(e) => handleMouseDown(e, chair, 'chair')}
                        onTouchStart={(e) => handleMouseDown(e, chair, 'chair')}
                        onClick={(e) => { e.stopPropagation(); setSelectedItem({ ...chair, type: 'chair' }); }}
                        className={`absolute w-[3%] h-[4%] rounded-full flex items-center justify-center cursor-move transition-all touch-none
                          ${tableIsSelected ? 'z-[2]' : 'z-[15]'}
                          ${isSel
                            ? 'bg-primary border-2 border-primary shadow-[0_0_12px_rgba(234,88,12,0.5)] scale-125 !z-[25]'
                            : isAvailable
                              ? 'bg-emerald-800/60 border border-emerald-600/60 hover:border-emerald-400 hover:scale-110'
                              : 'bg-muted/40 border border-border/50 opacity-60'
                          }`}
                        style={{ left: `${pos.x}%`, top: `${pos.y}%` }}>
                        <span className="text-[7px] font-bold text-white/80 pointer-events-none">{chair.label.slice(-1)}</span>
                        {hasOverride && !isSel && <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-cyan-400 rounded-full pointer-events-none" />}
                      </div>
                    );
                  });
                })}

                {/* Empty */}
                {layout.tables.length === 0 && (layout.chairs || []).length === 0 && layout.features.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center text-muted-foreground pointer-events-none">
                    <div className="text-center">
                      <Square className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>Haz click en "Agregar Mesa + Sillas" y luego en el plano</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Table Dialog */}
      <Dialog open={showTableDialog} onOpenChange={setShowTableDialog}>
        <DialogContent className="sm:max-w-md bg-[#0a0a0a] border-border">
          <DialogHeader>
            <DialogTitle>Nueva Mesa + Sillas</DialogTitle>
            <DialogDescription>Configura la mesa y las sillas que tendrá</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre de la Mesa *</Label>
              <Input data-testid="new-table-label" value={newTable.label} onChange={(e) => setNewTable({ ...newTable, label: e.target.value })} placeholder="Ej: Mesa 1, VIP, Terraza..." className="bg-background border-border" />
            </div>
            <div className="space-y-2">
              <Label>Forma</Label>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setNewTable({ ...newTable, shape: 'square' })}
                  className={`flex items-center justify-center gap-2 p-3 rounded-lg border text-sm transition-all ${newTable.shape === 'square' ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:border-primary/40'}`}>
                  <Square className="w-4 h-4" /> Cuadrada
                </button>
                <button type="button" onClick={() => setNewTable({ ...newTable, shape: 'circle' })}
                  className={`flex items-center justify-center gap-2 p-3 rounded-lg border text-sm transition-all ${newTable.shape === 'circle' ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:border-primary/40'}`}>
                  <Circle className="w-4 h-4" /> Circular
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cantidad de Sillas</Label>
                <Input type="number" min="1" max="20" data-testid="new-table-chairs" value={newTable.chairCount} onChange={(e) => setNewTable({ ...newTable, chairCount: Math.max(1, parseInt(e.target.value) || 1) })} className="bg-background border-border" />
              </div>
              <div className="space-y-2">
                <Label>Precio por Silla ({event.currency})</Label>
                <Input type="number" min="0" data-testid="new-table-price" value={newTable.defaultPrice} onChange={(e) => setNewTable({ ...newTable, defaultPrice: parseInt(e.target.value) || 0 })} className="bg-background border-border" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Luego puedes editar el precio de cada silla individualmente seleccionándola en el plano.</p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowTableDialog(false)} className="border-border">Cancelar</Button>
            <Button onClick={handleAddTable} className="bg-primary hover:bg-accent" disabled={!newTable.label}>
              <Check className="w-4 h-4 mr-2" />Agregar Mesa
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LayoutEditor;
